import React from 'react';
import { Checkbox, Switch } from '@heroui/react';

const TOGGLE_LABELS = {
	maintenance: 'Show Maintenance Events',
	myonly: 'Show Only My Events',
	waiting: 'Show Waiting List',
	activityTypeColorScheme: 'Activity Type Color Scheme'
};

const TOGGLE_ORDER = ['maintenance', 'myonly', 'waiting', 'activityTypeColorScheme'];

/**
 * Config-driven bank of boolean filter controls. Emits partial updates
 * (`{ [key]: nextValue }`) through `onChange` — composition parent is
 * expected to merge into its filter state.
 *
 * `compact` (default false) is the single binary layout axis (epic 189bab79):
 *   - `false` → desktop inline / fluid sidebar: `Checkbox` + `flex-wrap` row
 *     (unchanged legacy behavior).
 *   - `true`  → mobile Filters Drawer (<1024): one vertical COLUMN of `Switch`
 *     toggles, each a full-width, ≥44px single label+control hit target.
 * The parent (`CalendarFilterBar`) owns `navCompact` and threads it in — this
 * component must NOT call `useBreakpoint` itself. Per composition-patterns
 * (`architecture-avoid-boolean-props`), do NOT add further boolean modes here;
 * a 2nd/3rd mode should become explicit variant components instead.
 */
function FilterToggles({ config = {}, values = {}, onChange, compact = false }) {
	const visible = TOGGLE_ORDER.filter(key => config[key]);
	if (visible.length === 0) return null;

	if (compact) {
		return (
			<div className="flex flex-col gap-3" data-testid="calendar-filter-toggles">
				{visible.map(key => (
					<Switch
						key={key}
						size="sm"
						isSelected={Boolean(values[key])}
						onValueChange={next => onChange && onChange({ [key]: next })}
						classNames={{
							// `base` is the <label> wrapper (the whole hit target):
							// label text on the left, switch on the right, full-width, ≥44px.
							base: 'flex flex-row-reverse w-full max-w-full justify-between items-center gap-3 min-h-[44px] m-0 py-1',
							label: '!font-normal'
						}}
					>
						{TOGGLE_LABELS[key]}
					</Switch>
				))}
			</div>
		);
	}

	return (
		<div
			className="flex flex-wrap items-center gap-x-6 gap-y-2"
			data-testid="calendar-filter-toggles"
		>
			{visible.map(key => (
				<Checkbox
					key={key}
					size="sm"
					isSelected={Boolean(values[key])}
					onValueChange={next => onChange && onChange({ [key]: next })}
					classNames={{ label: '!font-normal' }}
				>
					{TOGGLE_LABELS[key]}
				</Checkbox>
			))}
		</div>
	);
}

export default FilterToggles;
export { TOGGLE_LABELS, TOGGLE_ORDER };
